<script lang="ts">
	import SbRichtext from '../components/sbRichtext.svelte';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import type { AddToCartButtonProps } from './cart/addToCartButton.svelte';
	import type { ButtonV3Props } from './buttonV3.svelte';
	import { isEmpty } from '$lib/utils/storyblok/richtext';
	import type { EppoData } from '../types';
	import type { SimpleImageBlock } from './simpleImageBlock.svelte';
	import RotatingText from '../components/rotatingText.svelte';
	import DivWrapper from '../components/divWrapper.svelte';

	interface Props {
		content: ISbStoryData<{
			bg_color: string;
			asset: Array<SimpleImageBlock>;
			headline_color: string;
			headline: ISbRichtext;
			text_rotation_items: Array<{ text: ISbRichtext }>;
			subheadline?: ISbRichtext;
			primary_cta: Array<AddToCartButtonProps | ButtonV3Props | EppoData>;
			secondary_cta?: Array<AddToCartButtonProps | ButtonV3Props | EppoData>;
			overlay?: boolean;
			text_rotation_delay?: string;
		}>['content'];
	}

	let { content }: Props = $props();
</script>

<section
	use:editable={content}
	id="hero"
	data-testid="hero-section"
	class="relative flex w-full flex-col justify-center lg:justify-center"
	style={`background-color: ${content.bg_color ?? 'transparent'}`}
>
	{#each content.asset as asset}
		<div class="absolute inset-0">
			<Blok content={asset} />
		</div>
	{/each}

	{#if content.overlay}
		<div class="pointer-events-none absolute inset-0 bg-black/20"></div>
	{/if}

	<PageGrid16
		margin
		class="relative flex h-full items-end lg:items-center lg:justify-center"
		gridClass="py-12 auto-rows-max"
	>
		<div
			class="col-span-full flex flex-col font-rhymes text-[50px]/[100%] tracking-[-1px] lg:col-start-2 lg:col-end-[16] lg:gap-y-1 lg:text-center lg:text-[102px]/[92%] lg:tracking-[-2.04px]"
		>
			<div style={`color: ${content.headline_color ?? 'white'}`}>
				<SbRichtext text={content.headline} />
			</div>
			<RotatingText
				class="text-white"
				items={content.text_rotation_items.map((item) => item.text)}
				delay={parseInt(content.text_rotation_delay ?? '2500', 10)}
			/>
		</div>
		{#if content.subheadline && !isEmpty(content.subheadline)}
			<div
				class="col-span-full mt-4 text-pretty text-[20px]/[140%] tracking-[-.2px] text-white lg:col-start-6 lg:col-end-[12] lg:mt-5 lg:text-center lg:text-[24px]/[140%] lg:tracking-[-.24px]"
			>
				<SbRichtext text={content.subheadline} />
			</div>
		{/if}
		<div class="col-span-full mt-6 flex w-full gap-x-3 lg:mt-9 lg:justify-center">
			{#each content.primary_cta as cta}
				<Blok content={cta} />
			{/each}
			{#each content.secondary_cta ?? [] as cta}
				<Blok content={cta} />
			{/each}
		</div>
	</PageGrid16>
</section>

<style lang="scss">
	section {
		height: calc(100svh - var(--banner-height));
		transition-property: padding-top, height;
		transition-duration: 0.2s;
		padding-top: var(--banner-height);
	}
</style>
